import type { Experiments, IPlatformLogger, IPropsStore, ISessionManager } from '@wix/thunderbolt-symbols'
import {
	ExperimentsSymbol,
	LoggerSymbol,
	METASITE_APP_DEF_ID,
	Props,
	SiteFeatureConfigSymbol,
} from '@wix/thunderbolt-symbols'
import { named, withDependencies } from '@wix/thunderbolt-ioc'
import { SessionManagerSymbol } from 'feature-session-manager'
import type {
	CustomCssData,
	CustomCssMapping,
	CustomCssSiteConfig,
	ICustomCssAPI,
	RequestFactoryByFilePath,
} from './types'
import { createHttpClient } from '@wix/http-client'
import { GLOBAL_CSS_NAMESPACE, GLOBAL_CSS_SELECTOR_PREFIX } from './constants'
import { getCssContentDev, getCssContent } from '@wix/ambassador-velo-css-v1-css-content-http/http'
import type { ViewEnvironment } from '@wix/ambassador-velo-css-v1-css-content-http/types'
import { name } from './symbols'
import { isExperimentOpen } from '@wix/thunderbolt-experiments'

const getInstance = (getAppInstanceByAppDefId: ISessionManager['getAppInstanceByAppDefId']) => () =>
	getAppInstanceByAppDefId(METASITE_APP_DEF_ID) || ''

export const CustomCssApi = withDependencies(
	[Props, SessionManagerSymbol, LoggerSymbol, ExperimentsSymbol, named(SiteFeatureConfigSymbol, name)],
	(
		props: IPropsStore,
		sessionManager: ISessionManager,
		logger: IPlatformLogger,
		experiments: Experiments,
		siteConfig: CustomCssSiteConfig
	): ICustomCssAPI => {
		const getAppInstance = getInstance(sessionManager.getAppInstanceByAppDefId)
		const isSSR = !process.env.browser
		const useBaseUrlExperimentEnabled = isExperimentOpen(experiments, 'specs.thunderbolt.customCssUseBaseUrl')
		// http-client reads baseURL from the client options only, and defaults SSR to http://www.wix.com.
		// The browser already resolves against the current origin, so this is SSR-only.
		const baseURL = isSSR && useBaseUrlExperimentEnabled ? siteConfig.baseUrl : undefined
		const httpClient = createHttpClient({
			getAppToken: getAppInstance,
			isSSR,
			...(baseURL ? { baseURL } : {}),
		})
		const defaultNamespace = GLOBAL_CSS_NAMESPACE
		const customCssStyleElements: { [appDefId: string]: HTMLStyleElement } = {}

		const fetchStylesByFilePath = async (
			gridAppId: string,
			requestFactory: RequestFactoryByFilePath,
			namespace: string = defaultNamespace,
			filePath: string,
			viewEnvironment?: ViewEnvironment
		) => {
			if (gridAppId) {
				logger.interactionStarted('get_page_css')
				try {
					const request = requestFactory({
						gridAppId,
						filePath,
						selectorPrefix: GLOBAL_CSS_SELECTOR_PREFIX,
						namespace,
						viewEnvironment,
					})

					const cssCode = await httpClient
						.request(request)
						.then((response) => response.data.cssContent?.content)
						.catch((error) => {
							if (error?.response?.status === 404) {
								// User possibly deleted CSS file
								return ''
							} else {
								throw new Error(error)
							}
						})
					logger.interactionEnded('get_page_css')
					return `/* Users Custom CSS code */\n ${cssCode}`
				} catch (e) {
					console.error('failed to fetch Custom CSS', e)
				}
			}
		}

		const fetchStyles = async (
			gridAppId: string,
			filePath: string,
			viewEnvironment?: ViewEnvironment,
			namespace?: string
		) => {
			const request = viewEnvironment ? getCssContentDev : getCssContent
			return fetchStylesByFilePath(gridAppId, request, namespace, filePath, viewEnvironment)
		}

		const applyStyles = (customCssMapping: CustomCssMapping) => {
			const appDefIds = Object.keys({ ...customCssStyleElements, ...customCssMapping })

			appDefIds.forEach((appDefId) => {
				const existingElement = customCssStyleElements[appDefId]
				const newCss = customCssMapping[appDefId]

				if (newCss) {
					if (existingElement) {
						existingElement.innerHTML = newCss
					} else {
						const newStyleElement = document.createElement('style')
						newStyleElement.id = `custom-css-${appDefId}`
						newStyleElement.innerHTML = newCss
						document.head.appendChild(newStyleElement)
						customCssStyleElements[appDefId] = newStyleElement
					}
				} else if (existingElement) {
					document.head.removeChild(existingElement)
					delete customCssStyleElements[appDefId]
				}
			})
		}
		const buildCustomCssMapping = async (
			appsInPage: Array<string>,
			appsWithCustomCss: { [appDefId: string]: CustomCssData },
			viewEnvironment?: ViewEnvironment
		) => {
			const cssToInject: CustomCssMapping = {}
			for (const appDefId of appsInPage) {
				const cssData = appsWithCustomCss[appDefId]
				if (cssData) {
					const css = await fetchStyles(
						cssData.gridId,
						cssData.filePath,
						viewEnvironment,
						`#SITE_CONTAINER #main_MF .css-editing-scope .custom-css-${appDefId}`
					)
					if (css) {
						cssToInject[appDefId] = css
					}
				}
			}
			return cssToInject
		}

		const updateAppsCustomCss = async (
			appsInPage: Array<string>,
			appsWithCustomCss: { [appDefId: string]: CustomCssData },
			viewEnvironment?: ViewEnvironment
		) => {
			const cssToInject: CustomCssMapping = await buildCustomCssMapping(appsInPage, appsWithCustomCss, viewEnvironment)
			return cssToInject
		}

		return {
			fetchStyles,
			applyStyles,
			updateAppsCustomCss,
		}
	}
)
